import { z } from 'zod'

import { m } from '@/lib/paraglide/messages'

/** Chooser form: `method` starts empty so nobody confirms by default. */
export interface DeliverySettlementFormValues {
    method: '' | 'cash' | 'bank_transfer' | 'credit'
    /**
     * Money the driver holds (cash, or a transfer into the driver's own
     * account). Prefilled with the amount due — customers usually pay in full;
     * blank = the full remaining debt.
     */
    cash_amount: string
    /** Only with bank_transfer: HTX account or the driver's own (M-133). */
    transfer_target: '' | 'htx' | 'driver'
    collect_fee: boolean
}

export function emptyDeliverySettlement(due = 0): DeliverySettlementFormValues {
    // Fee collection defaults ON: the fee is owed, opting out is the exception.
    return {
        method: '',
        cash_amount: due > 0 ? String(due) : '',
        transfer_target: '',
        collect_fee: true,
    }
}

export function createDeliverySettlementSchema() {
    return z
        .object({
            method: z.enum(['', 'cash', 'bank_transfer', 'credit']),
            cash_amount: z.string(),
            transfer_target: z.enum(['', 'htx', 'driver']),
            collect_fee: z.boolean(),
        })
        .superRefine((v, ctx) => {
            if (v.method === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['method'],
                    message: m.order_settlement_method_required(),
                })
            }
            if (v.method === 'bank_transfer' && v.transfer_target === '') {
                ctx.addIssue({
                    code: 'custom',
                    path: ['transfer_target'],
                    message: m.order_settlement_transfer_target_required(),
                })
            }
            const cash = v.cash_amount.trim()
            if (!holdsMoney(v) || cash === '') return
            const amount = Number(cash)
            if (!Number.isFinite(amount) || amount <= 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: ['cash_amount'],
                    message: m.order_settlement_cash_amount_invalid(),
                })
            }
        })
}

/** The driver holds the money: cash, or a transfer into the driver's account. */
export function holdsMoney(
    v: Pick<DeliverySettlementFormValues, 'method' | 'transfer_target'>,
): boolean {
    return (
        v.method === 'cash' ||
        (v.method === 'bank_transfer' && v.transfer_target === 'driver')
    )
}
